import { Component } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { LoaderService } from '../../../core/services/loader.service';
import { UmdService } from '../../../core/services/umd.services';
import { SharedService } from '../../../shared/shared.service';
import { SweetAlertIcon, UtilityType } from '../../../shared/constants/enum';
import { Validations } from '../../../shared/constants/validations';

@Component({
  selector: 'app-rp-create-ev-meter',
  templateUrl: './rp-create-ev-meter.component.html',
  styleUrl: './rp-create-ev-meter.component.scss',
  standalone: false,
})
export class RpCreateEvMeterComponent {
  evMeterForm!: UntypedFormGroup;
  submitted = false;
  currentUser: any;
  projectId: any;
  evMeterData: any;
  configData: any;
  evUtilityId: any;
  evSupplierItems: any[] = [];
  statusItems: any[] = [];
  breadCrumbItems: Array<{}> = [];
  validations = Validations.UmdCreatePage;
  private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';
  private readonly serialRegex = /^[A-Z0-9]+$/;
  private readonly macRegex = /^[0-9A-F]+$/;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private loaderService: LoaderService,
    private sharedService: SharedService,
    private umdService: UmdService,
    private router: Router,
  ) {
    this.currentUser = JSON.parse(localStorage.getItem('currentUser') ?? '{}');
    this.projectId = this.currentUser?.project?.id;
    this.evMeterData = history.state?.umdData;
    this.configData = history.state?.configData;
    this.breadCrumbItems = [
      { label: 'Utilities', url: 'utilities', active: true },
      { label: this.isEdit ? 'Edit EV Meter' : 'Add EV Meter' },
    ];
  }

  get isEdit(): boolean {
    return !!this.evMeterData?.id;
  }

  ngOnInit(): void {
    this.createForm();
    if (this.configData) {
      this.bindConfigData(this.configData);
      this.patchForm();
      return;
    }

    this.getAllConfigData();
  }

  private createForm(): void {
    this.evMeterForm = this.formBuilder.group({
      projectId: [this.projectId],
      utilityMasterId: [null, Validators.required],
      meterSerialNo: [
        '',
        [
          Validators.required,
          Validators.pattern(this.serialRegex),
          Validators.maxLength(16),
          Validators.minLength(7),
        ],
      ],
      macAddress: [
        '',
        [
          Validators.required,
          Validators.pattern(this.macRegex),
          Validators.maxLength(16),
          Validators.minLength(16),
        ],
      ],
      status: [null, Validators.required],
      supplierId: [null, Validators.required],
    });
  }

  private getAllConfigData(): void {
    this.loaderService.setLoading(true);
    this.sharedService
      .getAllConfigData(['suppliers', 'utilities', 'statuses'])
      .subscribe({
        next: (res) => {
          this.loaderService.setLoading(false);
          if (
            res.statusCode === 200 &&
            res.message === 'Config data retrieved successfully.'
          ) {
            this.configData = res.data;
            this.bindConfigData(this.configData);
            this.patchForm();
          }
        },
        error: (err) => {
          this.loaderService.setLoading(false);
          this.sharedService.showMessageDialog(
            'Error',
            this.sharedService.getApiErrorMessage(
              err,
              'Unable to load EV meter configuration.',
            ),
            SweetAlertIcon.ERROR,
          );
        },
      });
  }

  private bindConfigData(configData: any): void {
    this.evUtilityId = configData?.utilities?.find(
      (item: any) =>
        item?.name?.toString().trim().toLowerCase() ===
        UtilityType.EV.toLowerCase(),
    )?.id;

    this.evSupplierItems =
      configData?.suppliers?.filter(
        (item: any) =>
          item?.master?.name?.toString().trim().toLowerCase() ===
          UtilityType.EV.toLowerCase(),
      ) ?? [];
    this.statusItems = configData?.statuses ?? [];
    this.evMeterForm.get('utilityMasterId')?.setValue(this.evUtilityId);
  }

  private patchForm(): void {
    if (!this.isEdit) {
      return;
    }

    this.evMeterForm.patchValue({
      projectId: this.projectId,
      utilityMasterId: this.evMeterData?.utility?.id ?? this.evUtilityId,
      meterSerialNo: this.evMeterData?.meterSerialNo ?? '',
      macAddress: this.evMeterData?.macAddress ?? '',
      status: this.evMeterData?.status ?? null,
      supplierId:
        this.evMeterData?.supplierId &&
        this.evMeterData.supplierId !== this.emptyGuid
          ? this.evMeterData.supplierId
          : null,
    });
  }

  save(type: 'Create' | 'Update'): void {
    this.submitted = true;
    this.evMeterForm.markAllAsTouched();

    if (this.evMeterForm.invalid) {
      return;
    }

    const formValue = this.evMeterForm.getRawValue();
    const request = {
      projectId: formValue.projectId,
      utilityMasterId: formValue.utilityMasterId,
      macAddress: formValue.macAddress,
      status: formValue.status,
      meterSerialNo: formValue.meterSerialNo,
      supplierId: formValue.supplierId,
      mcbMainPower: null,
      gatewayInformationId: null,
      gatewayName: '',
      isWiredCommunicationDevice: false,
      valveRelayStatus: false,
      deviceId: '',
    };

    this.loaderService.setLoading(true);
    const request$ =
      type === 'Update'
        ? this.umdService.updateUmd(request, this.evMeterData.id)
        : this.umdService.createUmds(request);

    request$.subscribe({
      next: (response) => {
        this.loaderService.setLoading(false);
        if (response && (response.statusCode === 201 || response.statusCode === 200)) {
          this.sharedService.showMessageDialog(
            'Success',
            response.message,
            SweetAlertIcon.SUCCESS,
          );
          this.router.navigate(['/utilities'], { state: { selectedIndex: 1 } });
          return;
        }

        this.sharedService.showMessageDialog(
          'Failure',
          response?.message ?? 'Unable to save EV meter.',
          SweetAlertIcon.ERROR,
        );
      },
      error: (err) => {
        this.loaderService.setLoading(false);
        this.sharedService.showMessageDialog(
          this.sharedService.getApiErrorMessage(
            err,
            'Unable to save EV meter.',
          ),
          '',
          SweetAlertIcon.ERROR,
        );
      },
    });
  }

  cancel(): void {
    this.router.navigate(['/utilities'], { state: { selectedIndex: 1 } });
  }

  get controls() {
    return this.evMeterForm.controls;
  }

  isClearable(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed !== '' && trimmed !== this.emptyGuid && trimmed !== '-';
    }

    return true;
  }
}
